import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Heart, Cloud, Flame, Users, ChevronRight, Check, Lock, Send, ArrowLeft, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";
import { AppLayout } from "../components/AppLayout";
import { SpecialCategoryConsent } from "../components/ComplianceGates";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = { brain: Brain, heart: Heart, cloud: Cloud, flame: Flame, users: Users };

export default function TherapyPage() {
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [available, setAvailable] = useState(false);
  const [comingSoon, setComingSoon] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activePath, setActivePath] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);
  const token = localStorage.getItem("omni_token");
  const [scConsent, setScConsent] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    fetchPaths();
    fetch(`${API}/api/consent/status`, { headers })
      .then(r => r.json())
      .then(d => setScConsent(d.special_category?.granted === true))
      .catch(() => setScConsent(true));
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const fetchPaths = async () => {
    try {
      const res = await fetch(`${API}/api/therapy/paths`, { headers });
      const data = await res.json();
      setPaths(data.paths || []);
      setAvailable(data.available);
      setComingSoon(data.coming_soon === true);
    } catch { toast.error("Failed to load therapy paths"); }
    setLoading(false);
  };

  const enrollInPath = async (pathId) => {
    try {
      const res = await fetch(`${API}/api/therapy/paths/${pathId}/enroll`, { method: "POST", headers });
      const data = await res.json();
      if (res.ok) {
        toast.success("Journey started!");
        fetchPaths();
        openPath(pathId);
      } else { toast.error(data.detail || "Failed to enroll"); }
    } catch { toast.error("Failed to start journey"); }
  };

  const openPath = async (pathId) => {
    try {
      const res = await fetch(`${API}/api/therapy/paths/${pathId}`, { headers });
      const data = await res.json();
      setActivePath(data);
      setActiveStep(null);
      setChatMessages([]);
    } catch { toast.error("Failed to load path details"); }
  };

  const openStep = (step) => {
    setActiveStep(step);
    setChatMessages([{ role: "ai", content: step.prompt }]);
  };

  const sendChat = async () => {
    if (!chatInput.trim() || !activePath || !activeStep) return;
    const msg = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: msg }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/api/therapy/paths/${activePath.path.id}/step/${activeStep.step}/chat`, {
        method: "POST", headers, body: JSON.stringify({ message: msg }),
      });
      const data = await res.json();
      setChatMessages(prev => [...prev, { role: "ai", content: data.response }]);
    } catch { setChatMessages(prev => [...prev, { role: "ai", content: "I'm here with you. Could you try again?" }]); }
    setSending(false);
  };

  const completeStep = async () => {
    if (!activePath || !activeStep) return;
    try {
      const res = await fetch(`${API}/api/therapy/paths/${activePath.path.id}/step/${activeStep.step}/complete`, {
        method: "POST", headers, body: JSON.stringify({ notes: "" }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.path_completed ? "Journey complete!" : "Step completed!");
        openPath(activePath.path.id);
        setActiveStep(null);
        setChatMessages([]);
      }
    } catch { toast.error("Failed to complete step"); }
  };

  // Special category consent gate (GDPR Art. 9) — skipped while paths are set aside
  if (scConsent === false && !comingSoon) {
    return (
      <AppLayout>
        <div className="flex-1 overflow-y-auto p-6">
          <SpecialCategoryConsent onGranted={() => setScConsent(true)} />
        </div>
      </AppLayout>
    );
  }

  // Step chat view
  if (activeStep && activePath) {
    return (
      <AppLayout>
        <div className="flex flex-col h-full max-h-[calc(100vh-64px)]">
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border/30 bg-card/50 backdrop-blur-sm flex-shrink-0">
            <button onClick={() => { setActiveStep(null); setChatMessages([]); }} className="p-1.5 rounded-lg hover:bg-muted/50" data-testid="therapy-back-to-steps">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold truncate">Step {activeStep.step}: {activeStep.title}</p>
              <p className="text-[10px] text-muted-foreground truncate">{activePath.path.name}</p>
            </div>
            <button data-testid="therapy-complete-step" onClick={completeStep} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-1">
              <Check className="w-3 h-3" /> Complete
            </button>
          </div>

          {/* Chat area */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {chatMessages.map((m, i) => (
              <div key={i} className={cn("max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed", m.role === "user" ? "ml-auto bg-primary text-primary-foreground rounded-br-md" : "mr-auto bg-muted/50 rounded-bl-md")}>
                {m.content}
              </div>
            ))}
            {sending && (
              <div className="mr-auto bg-muted/50 rounded-2xl rounded-bl-md p-3 max-w-[85%]">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-2 border-t border-border/30 bg-card/50 backdrop-blur-sm flex-shrink-0">
            <form onSubmit={e => { e.preventDefault(); sendChat(); }} className="flex items-end gap-2">
              <textarea
                data-testid="therapy-chat-input"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder="Share your thoughts..."
                className="flex-1 resize-none rounded-xl border border-border/40 bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/40 min-h-[40px] max-h-[100px]"
                rows={1}
              />
              <button type="submit" disabled={!chatInput.trim() || sending} className="p-2.5 rounded-xl bg-primary text-primary-foreground disabled:opacity-40" data-testid="therapy-send-btn">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Path detail view — steps
  if (activePath) {
    const p = activePath.path;
    const steps = activePath.steps || [];
    const Icon = ICON_MAP[p.icon] || Brain;
    return (
      <AppLayout>
        <div className="p-4 max-w-2xl mx-auto">
          <button onClick={() => setActivePath(null)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="therapy-back-to-list">
            <ArrowLeft className="w-3.5 h-3.5" /> All Paths
          </button>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: p.color + "20" }}>
              <Icon className="w-6 h-6" style={{ color: p.color }} />
            </div>
            <div>
              <h1 className="text-lg font-semibold">{p.name}</h1>
              <p className="text-xs text-muted-foreground">{p.description}</p>
            </div>
          </div>

          <div className="space-y-2">
            {steps.map((step, i) => {
              const isCompleted = step.completed;
              const isCurrent = !isCompleted && (i === 0 || steps[i - 1]?.completed);
              return (
                <button
                  key={step.step}
                  data-testid={`therapy-step-${step.step}`}
                  onClick={() => { if (isCompleted || isCurrent) openStep(step); }}
                  disabled={!isCompleted && !isCurrent}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all",
                    isCompleted ? "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200/50 dark:border-emerald-800/30" :
                    isCurrent ? "bg-primary/5 border-primary/30 hover:shadow-md hover:-translate-y-0.5" :
                    "opacity-50 cursor-not-allowed border-border/30"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold",
                    isCompleted ? "bg-emerald-500 text-white" :
                    isCurrent ? "bg-primary text-primary-foreground" :
                    "bg-muted text-muted-foreground"
                  )}>
                    {isCompleted ? <Check className="w-4 h-4" /> : step.step}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{step.title}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{step.description}</p>
                  </div>
                  {(isCompleted || isCurrent) && <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      </AppLayout>
    );
  }

  // Path listing view
  return (
    <AppLayout>
      <div className="p-4 max-w-2xl mx-auto">
        <div className="mb-5">
          <h1 className="text-xl font-semibold">{comingSoon ? "Guided Journeys" : "Therapy Paths"}</h1>
          <p className="text-sm text-muted-foreground mt-1">Guided journeys for mental wellness and personal growth</p>
        </div>

        {comingSoon ? (
          <div data-testid="therapy-coming-soon" className="p-8 rounded-2xl border border-blue-200/50 dark:border-blue-800/30 bg-blue-50/50 dark:bg-blue-950/20 text-center">
            <Brain className="w-10 h-10 mx-auto text-blue-500 mb-3" />
            <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-blue-500/10 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full mb-3">Coming Soon</span>
            <h2 className="text-base font-semibold mb-2">Guided self-help paths are on their way</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">We're completing regulatory registrations worldwide before launching guided journeys — so we can bring them to you safely. In the meantime, The Friend is always here for emotional support, and mood tracking keeps your insights flowing.</p>
          </div>
        ) : (
        <>
        {!available && (
          <div data-testid="therapy-upgrade-banner" className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30">
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium flex items-center gap-2">
              <Lock className="w-4 h-4" /> Therapy paths require Omni+ or higher
            </p>
            <button onClick={() => navigate("/pricing")} className="text-xs text-amber-600 dark:text-amber-400 underline mt-1">View plans</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-3">
            {paths.map(p => {
              const Icon = ICON_MAP[p.icon] || Brain;
              const progress = p.total_steps > 0 ? Math.round((p.current_step / p.total_steps) * 100) : 0;
              return (
                <div key={p.id} data-testid={`therapy-path-${p.id}`} className="p-4 rounded-xl border border-border/50 bg-card hover:shadow-md transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: p.color + "20" }}>
                      <Icon className="w-5 h-5" style={{ color: p.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">{p.name}</h3>
                        {p.completed && <span className="text-[9px] bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded-full font-medium">Completed</span>}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{p.description}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[10px] text-muted-foreground">{p.total_steps} steps</span>
                        <span className="text-[10px] text-muted-foreground">{p.duration}</span>
                        {p.enrolled && (
                          <div className="flex items-center gap-1.5 flex-1">
                            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-[80px]">
                              <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: p.color }} />
                            </div>
                            <span className="text-[10px] font-medium" style={{ color: p.color }}>{progress}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {available && (
                      <button
                        data-testid={`therapy-${p.enrolled ? "continue" : "start"}-${p.id}`}
                        onClick={() => p.enrolled ? openPath(p.id) : enrollInPath(p.id)}
                        className={cn(
                          "px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex-shrink-0",
                          p.enrolled ? "bg-primary/10 text-primary hover:bg-primary/20" : "bg-primary text-primary-foreground hover:opacity-90"
                        )}
                      >
                        {p.enrolled ? (p.completed ? "Review" : "Continue") : "Start"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </AppLayout>
  );
}
